import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './MealPlanner.css';

/* ── Types ── */

interface RecipeResult {
  id: string;
  nanoid: string;
  name: string;
  meal_type: string | null;
  cuisine?: string;
  description?: string | null;
  total_time_minutes?: number;
  prep_time_minutes?: number;
}

interface MealItem {
  tempId: string;
  recipe_id: string | null;
  recipe_nanoid: string | null;
  custom_meal_name: string | null;
  meal_type: 'breakfast' | 'lunch' | 'dinner' | 'snack' | null;
  servings: number;
  days: number;
  is_decide_for_me: boolean;
}

interface CookTime {
  date: string;
  time: string;
}

interface StapleItem {
  id: string;
  label: string;
  default_quantity: string | null;
  included?: boolean;
  quantity?: string;
}

/* ── Constants ── */

const STEP_LABELS = ['Meals', 'Portions', 'Add-Ons', 'Timing', 'Review'];
const TOTAL_STEPS = STEP_LABELS.length;

interface SuggestedStaple {
  label: string;
  defaultQty: string;
  category: string;
}

const SUGGESTED_STAPLES: SuggestedStaple[] = [
  // Dairy & Eggs
  { label: 'Milk',              defaultQty: '1 gallon',  category: 'Dairy & Eggs' },
  { label: 'Eggs',              defaultQty: '1 dozen',   category: 'Dairy & Eggs' },
  { label: 'Butter',            defaultQty: '1 stick',   category: 'Dairy & Eggs' },
  { label: 'Shredded cheddar',  defaultQty: '8 oz',      category: 'Dairy & Eggs' },
  { label: 'Greek yogurt',      defaultQty: '32 oz',     category: 'Dairy & Eggs' },
  // Produce
  { label: 'Garlic',            defaultQty: '1 head',    category: 'Produce' },
  { label: 'Yellow onions',     defaultQty: '3 lbs',     category: 'Produce' },
  { label: 'Lemons',            defaultQty: '4',         category: 'Produce' },
  { label: 'Mixed fresh fruit', defaultQty: '',          category: 'Produce' },
  // Pantry
  { label: 'Olive oil',         defaultQty: '1 bottle',  category: 'Pantry' },
  { label: 'Oats',              defaultQty: '1 lb',      category: 'Pantry' },
  { label: 'Almond butter',     defaultQty: '1 jar',     category: 'Pantry' },
  // Beverages & Bread
  { label: 'Coffee',            defaultQty: '',          category: 'Beverages' },
  { label: 'Sparkling water',   defaultQty: '1 case',    category: 'Beverages' },
  { label: 'Sourdough bread',   defaultQty: '1 loaf',    category: 'Bread' },
];

const MEAL_TYPE_OPTIONS: Array<{ value: MealItem['meal_type']; label: string }> = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch',     label: 'Lunch' },
  { value: 'dinner',    label: 'Dinner' },
  { value: 'snack',     label: 'Snack' },
  { value: null,        label: 'Any' },
];

// Meal-type filters for the "Pick something for me" picker. Lunch, dinner and
// "any" recipes are folded into a single option; the value is sent to the
// suggest endpoint (comma-separated for the combined bucket).
const SUGGEST_MEAL_TYPES: Array<{ value: string | null; label: string }> = [
  { value: null,             label: 'Any' },
  { value: 'breakfast',      label: 'Breakfast' },
  { value: 'lunch,dinner',   label: 'Lunch / Dinner' },
  { value: 'snack',          label: 'Snack' },
];

// Keywords for nutrition feedback on review step
const NUTRITION_KEYWORDS: Record<string, string[]> = {
  vegetables: ['salad', 'veggie', 'vegetable', 'broccoli', 'spinach', 'kale', 'carrot', 'zucchini', 'asparagus', 'stir.fry', 'roasted veg', 'green', 'cauliflower', 'cucumber', 'tomato', 'pepper', 'mushroom', 'soup', 'minestrone', 'ratatouille'],
  fruit: ['fruit', 'berry', 'apple', 'banana', 'orange', 'mango', 'smoothie', 'acai', 'avocado'],
  protein: ['chicken', 'beef', 'salmon', 'fish', 'tofu', 'shrimp', 'egg', 'turkey', 'pork', 'steak', 'lentil', 'bean', 'tempeh'],
  grains: ['rice', 'pasta', 'grain', 'quinoa', 'oat', 'bread', 'noodle', 'barley', 'farro', 'wrap', 'tortilla', 'bowl'],
};

function getMissingCategories(meals: MealItem[]): string[] {
  const allText = meals.map(m =>
    (m.custom_meal_name || '').toLowerCase()
  ).join(' ');

  const missing: string[] = [];
  for (const [category, keywords] of Object.entries(NUTRITION_KEYWORDS)) {
    if (!keywords.some(kw => allText.includes(kw))) {
      missing.push(category);
    }
  }
  // Only flag if at least 3 meals are non-"decide for me" so we have enough signal
  const definedMeals = meals.filter(m => !m.is_decide_for_me);
  return definedMeals.length >= 2 ? missing : [];
}

const FEEDBACK_COPY: Record<string, string> = {
  vegetables: 'Looks light on vegetables — worth adding a veggie side or salad?',
  fruit: 'No fruit in sight — consider a smoothie or adding fresh fruit to your list.',
  protein: 'Plan looks light on protein — make sure you\'ve got a good protein source.',
  grains: 'No grains or complex carbs detected — rice, pasta, or quinoa can round things out.',
};

function mealIcon(item: MealItem): string {
  if (item.is_decide_for_me) return '?';
  switch (item.meal_type) {
    case 'breakfast': return 'B';
    case 'lunch':     return 'L';
    case 'dinner':    return 'D';
    case 'snack':     return 'S';
    default:          return '·';
  }
}

function mealDisplayName(item: MealItem): string {
  if (item.is_decide_for_me) return 'Decide for me';
  if (item.custom_meal_name) return item.custom_meal_name;
  return 'Unnamed meal';
}

function makeTempId() {
  return Math.random().toString(36).slice(2);
}

/* ── Demo mock data ── */

const DEMO_MEALS: MealItem[] = [
  { tempId: 'd1', recipe_id: null, recipe_nanoid: null, custom_meal_name: 'Sheet pan salmon with roasted veggies', meal_type: 'dinner',    servings: 2, days: 2, is_decide_for_me: false },
  { tempId: 'd2', recipe_id: null, recipe_nanoid: null, custom_meal_name: 'Chicken stir-fry with brown rice',       meal_type: 'dinner',    servings: 2, days: 2, is_decide_for_me: false },
  { tempId: 'd3', recipe_id: null, recipe_nanoid: null, custom_meal_name: null,                                     meal_type: null,        servings: 2, days: 1, is_decide_for_me: true  },
];

const DEMO_STAPLES: StapleItem[] = [
  { id: 'ds1', label: 'Eggs',         default_quantity: '1 dozen',  included: true,  quantity: '1 dozen' },
  { id: 'ds2', label: 'Greek yogurt', default_quantity: '32 oz',    included: true,  quantity: '32 oz' },
  { id: 'ds3', label: 'Coffee',       default_quantity: '',         included: true,  quantity: '' },
];

const DEMO_RECIPES: RecipeResult[] = [
  { id: 'r1', nanoid: 'rec001', name: 'Mediterranean Chicken Bowl', meal_type: 'lunch',     cuisine: 'Mediterranean' },
  { id: 'r2', nanoid: 'rec002', name: 'Black Bean Tacos',           meal_type: 'dinner',    cuisine: 'Mexican' },
  { id: 'r3', nanoid: 'rec003', name: 'Overnight Oats',             meal_type: 'breakfast', cuisine: undefined },
  { id: 'r4', nanoid: 'rec004', name: 'Lemon Herb Salmon',          meal_type: 'dinner',    cuisine: 'American' },
];

/* ── Component ── */

export default function MealPlannerPage() {
  const { nanoid } = useParams<{ nanoid: string }>();
  const isDemo = nanoid === 'demo';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [groceryNanoid, setGroceryNanoid] = useState<string | null>(null);

  // Step 0: Meals
  const [meals, setMeals] = useState<MealItem[]>([]);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addMode, setAddMode] = useState<'recipe' | 'all-recipe' | 'custom' | 'decide' | null>(null);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [recipeResults, setRecipeResults] = useState<RecipeResult[]>([]);
  const [allRecipeSearch, setAllRecipeSearch] = useState('');
  const [allRecipeResults, setAllRecipeResults] = useState<RecipeResult[]>([]);
  const [suggestions, setSuggestions] = useState<RecipeResult[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestMealType, setSuggestMealType] = useState<string | null>(null);
  const [customInput, setCustomInput] = useState('');
  const [customMealType, setCustomMealType] = useState<MealItem['meal_type']>('dinner');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const allSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 2: Add-Ons (merged Extras + Staples)
  const [staples, setStaples] = useState<StapleItem[]>([]);
  const [newStapleLabel, setNewStapleLabel] = useState('');
  const [extrasFreeform, setExtrasFreeform] = useState('');

  // Step 3: Timing
  const [orderDate, setOrderDate] = useState('');
  const [orderTime, setOrderTime] = useState('');
  const [cookTimes, setCookTimes] = useState<CookTime[]>([{ date: '', time: '' }]);

  const apiBase = import.meta.env.VITE_API_URL || '';

  /* ── Load existing plan state ── */
  useEffect(() => {
    if (isDemo) {
      setMeals(DEMO_MEALS);
      setLoading(false);
      return;
    }
    if (!nanoid) return;

    fetch(`${apiBase}/api/meal-plans/${nanoid}`)
      .then(r => r.json())
      .then(data => {
        if (!data.success) {
          if (data.error?.includes('expired') || data.error?.includes('Expired')) {
            setExpired(true);
          } else {
            setError(data.error || 'Unable to load meal planner.');
          }
          return;
        }

        const plan = data.data;

        if (plan.status === 'list_generated' && plan.grocery_list_nanoid) {
          setGroceryNanoid(plan.grocery_list_nanoid);
          setLoading(false);
          return;
        }

        if (Array.isArray(plan.items) && plan.items.length > 0) {
          setMeals(plan.items.map((i: any) => ({
            tempId: i.id,
            recipe_id: i.recipe_id,
            recipe_nanoid: i.recipe_nanoid || null,
            custom_meal_name: i.custom_meal_name,
            meal_type: i.meal_type || null,
            servings: i.servings || 2,
            days: i.days || 1,
            is_decide_for_me: Boolean(i.is_decide_for_me),
          })));
        }

        if (plan.order_timing) {
          // day_of_week stored in DB; can't reconstruct exact date, preserve time only
          setOrderTime(plan.order_timing.time || '');
        }

        if (Array.isArray(plan.cook_timing) && plan.cook_timing.length > 0) {
          setCookTimes(plan.cook_timing.map((ct: any) => ({
            date: '',
            time: ct.time || '',
          })));
        }

        if (plan.extras_freeform) setExtrasFreeform(plan.extras_freeform);

        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load meal planner. Please try again.');
        setLoading(false);
      });
  }, [nanoid]);

  /* ── Load staples via nanoid (no auth required) ── */
  useEffect(() => {
    if (isDemo) {
      setStaples(DEMO_STAPLES);
      return;
    }
    if (!nanoid) return;

    fetch(`${apiBase}/api/meal-plans/${nanoid}/staples`)
      .then(r => r.json())
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          setStaples(data.data.map((s: any) => ({
            ...s,
            included: true,
            quantity: s.default_quantity || '',
          })));
        }
      })
      .catch(() => {});
  }, [nanoid]);

  /* ── Recipe search via nanoid endpoint (no Firebase auth needed) ── */
  useEffect(() => {
    if (!recipeSearch.trim()) {
      setRecipeResults([]);
      return;
    }

    if (isDemo) {
      const q = recipeSearch.toLowerCase();
      setRecipeResults(DEMO_RECIPES.filter(r => r.name.toLowerCase().includes(q)));
      return;
    }

    if (!nanoid) return;

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `${apiBase}/api/meal-plans/${nanoid}/recipes?q=${encodeURIComponent(recipeSearch.trim())}`
        );
        const data = await r.json();
        if (data.success && Array.isArray(data.data)) {
          setRecipeResults(data.data.slice(0, 8));
        }
      } catch {
        setRecipeResults([]);
      }
    }, 300);
  }, [recipeSearch, nanoid]);

  /* ── All-recipe search ── */
  useEffect(() => {
    if (!allRecipeSearch.trim()) {
      setAllRecipeResults([]);
      return;
    }

    if (isDemo) {
      const q = allRecipeSearch.toLowerCase();
      setAllRecipeResults(DEMO_RECIPES.filter(r => r.name.toLowerCase().includes(q)));
      return;
    }

    if (!nanoid) return;
    if (allSearchTimeout.current) clearTimeout(allSearchTimeout.current);
    allSearchTimeout.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `${apiBase}/api/meal-plans/${nanoid}/recipes?mode=all&q=${encodeURIComponent(allRecipeSearch.trim())}`
        );
        const data = await r.json();
        if (data.success && Array.isArray(data.data)) {
          setAllRecipeResults(data.data.slice(0, 8));
        }
      } catch {
        setAllRecipeResults([]);
      }
    }, 300);
  }, [allRecipeSearch, nanoid]);

  /* ── Decide-for-me suggestions ── */
  async function fetchSuggestions(mealType: string | null) {
    setSuggestionsLoading(true);
    setSuggestions([]);

    if (isDemo) {
      await new Promise(r => setTimeout(r, 500));
      const types = mealType ? mealType.split(',').map(s => s.trim()) : [];
      const filtered = types.length > 0
        ? DEMO_RECIPES.filter(r => (r.meal_type && types.includes(r.meal_type)) || r.meal_type === null)
        : DEMO_RECIPES;
      setSuggestions(filtered.slice(0, 3));
      setSuggestionsLoading(false);
      return;
    }

    if (!nanoid) { setSuggestionsLoading(false); return; }

    try {
      const params = new URLSearchParams({ count: '3' });
      if (mealType) params.set('meal_type', mealType);
      const r = await fetch(`${apiBase}/api/meal-plans/${nanoid}/recipes/suggest?${params}`);
      const data = await r.json();
      if (data.success && Array.isArray(data.data)) {
        setSuggestions(data.data);
      }
    } catch {
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }

  /* ── Helpers ── */

  function addMealFromRecipe(recipe: RecipeResult) {
    if (meals.length >= 7) return;
    setMeals(prev => [...prev, {
      tempId: makeTempId(),
      recipe_id: recipe.id || null,
      recipe_nanoid: recipe.nanoid,
      custom_meal_name: recipe.name,
      meal_type: (recipe.meal_type as MealItem['meal_type']) || null,
      servings: 2,
      days: 1,
      is_decide_for_me: false,
    }]);
    setRecipeSearch('');
    setRecipeResults([]);
    setAddMode(null);
    setShowAddPanel(false);
  }

  function addCustomMeal() {
    if (!customInput.trim() || meals.length >= 7) return;
    setMeals(prev => [...prev, {
      tempId: makeTempId(),
      recipe_id: null,
      recipe_nanoid: null,
      custom_meal_name: customInput.trim(),
      meal_type: customMealType,
      servings: 2,
      days: 1,
      is_decide_for_me: false,
    }]);
    setCustomInput('');
    setAddMode(null);
    setShowAddPanel(false);
  }

  function removeMeal(tempId: string) {
    setMeals(prev => prev.filter(m => m.tempId !== tempId));
  }

  function updateMealField(tempId: string, field: 'servings' | 'days' | 'meal_type', val: any) {
    setMeals(prev => prev.map(m => m.tempId === tempId ? { ...m, [field]: val } : m));
  }

  function toggleStaple(id: string) {
    setStaples(prev => prev.map(s => s.id === id ? { ...s, included: !s.included } : s));
  }

  function updateStapleQty(id: string, qty: string) {
    setStaples(prev => prev.map(s => s.id === id ? { ...s, quantity: qty } : s));
  }

  async function addStaple() {
    if (!newStapleLabel.trim()) return;

    if (isDemo) {
      setStaples(prev => [...prev, {
        id: makeTempId(),
        label: newStapleLabel.trim(),
        default_quantity: null,
        included: true,
        quantity: '',
      }]);
      setNewStapleLabel('');
      return;
    }

    if (!nanoid) return;

    try {
      const r = await fetch(`${apiBase}/api/meal-plans/${nanoid}/staples`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: newStapleLabel.trim() }),
      });
      const data = await r.json();
      if (data.success) {
        setStaples(prev => [...prev, { ...data.data, included: true, quantity: '' }]);
      } else {
        setStaples(prev => [...prev, {
          id: makeTempId(),
          label: newStapleLabel.trim(),
          default_quantity: null,
          included: true,
          quantity: '',
        }]);
      }
    } catch {
      setStaples(prev => [...prev, {
        id: makeTempId(),
        label: newStapleLabel.trim(),
        default_quantity: null,
        included: true,
        quantity: '',
      }]);
    }
    setNewStapleLabel('');
  }

  function addSuggestedStaple(suggestion: SuggestedStaple) {
    const existing = staples.find(
      s => s.label.toLowerCase() === suggestion.label.toLowerCase()
    );
    if (existing) {
      setStaples(prev => prev.map(s =>
        s.label.toLowerCase() === suggestion.label.toLowerCase()
          ? { ...s, included: true, quantity: s.quantity || suggestion.defaultQty }
          : s
      ));
      return;
    }

    const tempItem: StapleItem = {
      id: makeTempId(),
      label: suggestion.label,
      default_quantity: suggestion.defaultQty || null,
      included: true,
      quantity: suggestion.defaultQty,
    };
    setStaples(prev => [...prev, tempItem]);

    if (!isDemo && nanoid) {
      fetch(`${apiBase}/api/meal-plans/${nanoid}/staples`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: suggestion.label, default_quantity: suggestion.defaultQty || null }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            setStaples(prev => prev.map(s =>
              s.id === tempItem.id ? { ...data.data, included: true, quantity: suggestion.defaultQty } : s
            ));
          }
        })
        .catch(() => {});
    }
  }

  function addCookTime() {
    setCookTimes(prev => [...prev, { date: '', time: '' }]);
  }

  function removeCookTime(idx: number) {
    setCookTimes(prev => prev.filter((_, i) => i !== idx));
  }

  function updateCookTime(idx: number, field: 'date' | 'time', val: string) {
    setCookTimes(prev => prev.map((ct, i) => i === idx ? { ...ct, [field]: val } : ct));
  }

  function dayOfWeekFromDate(dateStr: string): string {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  }

  /* ── Save draft progress ── */
  async function saveDraft() {
    if (isDemo || !nanoid) return;

    const body = {
      order_timing: orderDate ? { day_of_week: dayOfWeekFromDate(orderDate), time: orderTime } : null,
      cook_timing: cookTimes
        .filter(ct => ct.date)
        .map(ct => ({ day_of_week: dayOfWeekFromDate(ct.date), time: ct.time })),
      extras_freeform: extrasFreeform || null,
      extras_pills: [],
      items: meals.map((m, i) => ({
        recipe_id: m.recipe_id,
        custom_meal_name: m.custom_meal_name,
        meal_type: m.meal_type,
        servings: m.servings,
        days: m.days,
        is_decide_for_me: m.is_decide_for_me,
        sort_order: i,
      })),
    };

    await fetch(`${apiBase}/api/meal-plans/${nanoid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => {});
  }

  /* ── Navigation ── */
  async function goNext() {
    await saveDraft();
    setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  }

  function goBack() {
    setStep(s => Math.max(s - 1, 0));
  }

  /* ── Submit ── */
  async function handleSubmit() {
    if (isDemo) {
      setSubmitting(true);
      // Simulate generation delay so the spinner is visible
      await new Promise(r => setTimeout(r, 1200));
      setGroceryNanoid('demo');
      setSubmitting(false);
      return;
    }

    if (!nanoid) return;
    setSubmitting(true);

    await saveDraft();

    try {
      const r = await fetch(`${apiBase}/api/meal-plans/${nanoid}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await r.json();
      if (data.success && data.data?.grocery_list_nanoid) {
        setGroceryNanoid(data.data.grocery_list_nanoid);
      } else {
        setError(data.error || 'Submission failed. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  /* ── Render guards ── */

  if (expired) {
    return (
      <div className="meal-planner-page">
        <PublicHeader />
        <div className="meal-planner-container">
          <div className="mp-error">
            <p>This meal planning link has expired (7-day limit).</p>
            <p>Text Mosaime and ask for a new meal planner link.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="meal-planner-page">
        <PublicHeader />
        <div className="meal-planner-container">
          <div className="mp-loading">Loading your meal planner…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="meal-planner-page">
        <PublicHeader />
        <div className="meal-planner-container">
          <div className="mp-error">{error}</div>
        </div>
      </div>
    );
  }

  /* ── Success state ── */
  if (groceryNanoid) {
    return (
      <div className="meal-planner-page">
        <PublicHeader />
        <div className="meal-planner-container">
          {isDemo && <div className="demo-banner">Demo mode — no data was saved</div>}
          <div className="success-card">
            <h2>Your grocery list is ready!</h2>
            <p>We built your list based on all your meals and staples.{!isDemo && ' We\'ll also text you when it\'s time to order and cook.'}</p>
            <Link to={`/grocery/${groceryNanoid}`} className="btn-view-list">
              View Grocery List
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const canProceedStep: Record<number, boolean> = {
    0: meals.length > 0,
    1: true,
    2: true,
    3: true,
    4: true,
  };

  /* ── Render ── */
  return (
    <div className="meal-planner-page">
      <PublicHeader />
      <div className="meal-planner-container">

        {isDemo && <div className="demo-banner">Demo mode — changes are local only, nothing is saved</div>}

        {/* Stepper */}
        <div className="stepper">
          {STEP_LABELS.map((label, i) => (
            <React.Fragment key={i}>
              <div className="stepper-item">
                <div className={`step-dot${i === step ? ' active' : i < step ? ' completed' : ''}`}>
                  {i < step ? '✓' : i + 1}
                </div>
                <div className={`step-label${i === step ? ' active' : ''}`}>{label}</div>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div className={`step-connector${i < step ? ' done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 0: Meals ── */}
        {step === 0 && (
          <>
            <div className="step-header">
              <h2>What do you want to cook?</h2>
              <p>Add up to 7 meals. Pick from your saved recipes, describe a meal, or let us decide.</p>
            </div>

            <div className="meal-cards">
              {meals.map(m => (
                <div key={m.tempId} className="meal-card">
                  <div className="meal-card-icon">{mealIcon(m)}</div>
                  <div className="meal-card-info">
                    <div className="meal-card-name">{mealDisplayName(m)}</div>
                    {!m.is_decide_for_me && (
                      <select
                        className="meal-type-select"
                        value={m.meal_type || ''}
                        onChange={e => updateMealField(m.tempId, 'meal_type', e.target.value || null)}
                      >
                        {MEAL_TYPE_OPTIONS.map(o => (
                          <option key={String(o.value)} value={o.value || ''}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    )}
                    {m.is_decide_for_me && (
                      <div className="meal-card-meta">We'll pick for you</div>
                    )}
                  </div>
                  <button className="meal-card-remove" onClick={() => removeMeal(m.tempId)} aria-label="Remove">✕</button>
                </div>
              ))}
            </div>

            {meals.length < 7 && (
              <div className="add-meal-panel">
                {!showAddPanel ? (
                  <button className="btn-open-add" onClick={() => setShowAddPanel(true)}>
                    + Add a meal
                  </button>
                ) : (
                  <>
                    {/* Mode selector buttons */}
                    {addMode === null && (
                      <div className="add-meal-options">
                        <button className="add-meal-option" onClick={() => setAddMode('recipe')}>
                          Pick from my saved recipes
                        </button>
                        <button className="add-meal-option" onClick={() => setAddMode('all-recipe')}>
                          Search all recipes
                        </button>
                        <button className="add-meal-option" onClick={() => setAddMode('custom')}>
                          Describe a meal
                        </button>
                        <button
                          className="add-meal-option"
                          onClick={() => {
                            setAddMode('decide');
                            setSuggestMealType(null);
                            fetchSuggestions(null);
                          }}
                        >
                          Pick something for me
                        </button>
                      </div>
                    )}

                    {/* Saved recipes search */}
                    {addMode === 'recipe' && (
                      <div className="add-mode-panel">
                        <div className="add-mode-header">
                          <button className="add-mode-back" onClick={() => { setAddMode(null); setRecipeSearch(''); setRecipeResults([]); }}>← Back</button>
                          <span>Saved recipes</span>
                        </div>
                        <input
                          className="recipe-search-input"
                          type="text"
                          placeholder="Search your saved recipes…"
                          value={recipeSearch}
                          onChange={e => setRecipeSearch(e.target.value)}
                          autoFocus
                        />
                        {recipeResults.length > 0 && (
                          <div className="recipe-search-results">
                            {recipeResults.map(r => (
                              <div key={r.nanoid} className="recipe-search-result">
                                <div className="recipe-search-result-info">
                                  <a
                                    className="recipe-search-result-name"
                                    href={`/recipes/${r.nanoid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >{r.name}</a>
                                  <div className="recipe-search-result-meta">
                                    {r.meal_type ? r.meal_type.charAt(0).toUpperCase() + r.meal_type.slice(1) : 'Any'}
                                    {r.cuisine ? ` · ${r.cuisine}` : ''}
                                  </div>
                                </div>
                                <button
                                  className="btn-add-suggestion"
                                  onClick={() => addMealFromRecipe(r)}
                                  aria-label={`Add ${r.name}`}
                                >+ Add</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {recipeSearch.trim() && recipeResults.length === 0 && (
                          <div className="recipe-search-empty">
                            No saved recipes match. <button className="link-btn" onClick={() => { setAddMode('all-recipe'); setAllRecipeSearch(recipeSearch); setRecipeSearch(''); }}>Search all recipes instead →</button>
                          </div>
                        )}
                        {!recipeSearch.trim() && (
                          <div className="recipe-search-empty">Type to search your saved recipes.</div>
                        )}
                      </div>
                    )}

                    {/* All recipes search */}
                    {addMode === 'all-recipe' && (
                      <div className="add-mode-panel">
                        <div className="add-mode-header">
                          <button className="add-mode-back" onClick={() => { setAddMode(null); setAllRecipeSearch(''); setAllRecipeResults([]); }}>← Back</button>
                          <span>All recipes</span>
                        </div>
                        <input
                          className="recipe-search-input"
                          type="text"
                          placeholder="Search all recipes…"
                          value={allRecipeSearch}
                          onChange={e => setAllRecipeSearch(e.target.value)}
                          autoFocus
                        />
                        {allRecipeResults.length > 0 && (
                          <div className="recipe-search-results">
                            {allRecipeResults.map(r => (
                              <div key={r.nanoid} className="recipe-search-result">
                                <div className="recipe-search-result-info">
                                  <a
                                    className="recipe-search-result-name"
                                    href={`/recipes/${r.nanoid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >{r.name}</a>
                                  <div className="recipe-search-result-meta">
                                    {r.meal_type ? r.meal_type.charAt(0).toUpperCase() + r.meal_type.slice(1) : 'Any'}
                                    {r.cuisine ? ` · ${r.cuisine}` : ''}
                                  </div>
                                </div>
                                <button
                                  className="btn-add-suggestion"
                                  onClick={() => addMealFromRecipe(r)}
                                  aria-label={`Add ${r.name}`}
                                >+ Add</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {allRecipeSearch.trim() && allRecipeResults.length === 0 && (
                          <div className="recipe-search-empty">No recipes found for "{allRecipeSearch}".</div>
                        )}
                        {!allRecipeSearch.trim() && (
                          <div className="recipe-search-empty">Type to search the full recipe library.</div>
                        )}
                      </div>
                    )}

                    {/* Describe a custom meal */}
                    {addMode === 'custom' && (
                      <div className="add-mode-panel">
                        <div className="add-mode-header">
                          <button className="add-mode-back" onClick={() => { setAddMode(null); setCustomInput(''); }}>← Back</button>
                          <span>Describe a meal</span>
                        </div>
                        <div className="custom-meal-input">
                          <input
                            type="text"
                            placeholder='e.g. "Sheet pan salmon with veggies"'
                            value={customInput}
                            onChange={e => setCustomInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addCustomMeal()}
                            autoFocus
                          />
                          <select
                            className="custom-meal-type-select"
                            value={customMealType || ''}
                            onChange={e => setCustomMealType((e.target.value || null) as MealItem['meal_type'])}
                          >
                            {MEAL_TYPE_OPTIONS.map(o => (
                              <option key={String(o.value)} value={o.value || ''}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                          <button className="btn-add-meal" onClick={addCustomMeal} aria-label="Add">+</button>
                        </div>
                      </div>
                    )}

                    {/* Pick something for me — suggestion picker */}
                    {addMode === 'decide' && (
                      <div className="add-mode-panel">
                        <div className="add-mode-header">
                          <button className="add-mode-back" onClick={() => { setAddMode(null); setSuggestions([]); }}>← Back</button>
                          <span>Pick something for me</span>
                        </div>

                        <div className="suggest-filter-row">
                          <label className="suggest-filter-label">Meal type</label>
                          <div className="suggest-type-pills">
                            {SUGGEST_MEAL_TYPES.map(o => (
                              <button
                                key={String(o.value)}
                                className={`suggest-type-pill${suggestMealType === o.value ? ' active' : ''}`}
                                onClick={() => {
                                  setSuggestMealType(o.value);
                                  fetchSuggestions(o.value);
                                }}
                              >
                                {o.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {suggestionsLoading && (
                          <div className="suggest-loading">Finding options…</div>
                        )}

                        {!suggestionsLoading && suggestions.length === 0 && (
                          <div className="recipe-search-empty">No suggestions available. Try a different meal type.</div>
                        )}

                        {!suggestionsLoading && suggestions.length > 0 && (
                          <>
                            <div className="suggestion-cards">
                              {suggestions.map(s => (
                                <div key={s.nanoid} className="suggestion-card">
                                  <div className="suggestion-card-info">
                                    <a
                                      className="suggestion-card-name"
                                      href={`/recipes/${s.nanoid}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >{s.name}</a>
                                    <div className="suggestion-card-meta">
                                      {s.meal_type ? s.meal_type.charAt(0).toUpperCase() + s.meal_type.slice(1) : 'Any'}
                                      {s.cuisine ? ` · ${s.cuisine}` : ''}
                                      {s.total_time_minutes ? ` · ${s.total_time_minutes} min` : ''}
                                    </div>
                                    {s.description && (
                                      <div className="suggestion-card-desc">{s.description}</div>
                                    )}
                                  </div>
                                  <button
                                    className="btn-add-suggestion"
                                    onClick={() => addMealFromRecipe(s)}
                                    aria-label={`Add ${s.name}`}
                                  >+ Add</button>
                                </div>
                              ))}
                            </div>
                            <button
                              className="btn-refresh-suggestions"
                              onClick={() => fetchSuggestions(suggestMealType)}
                            >
                              Show different options
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {addMode === null && (
                      <button className="btn-cancel-add" onClick={() => { setShowAddPanel(false); }}>
                        Cancel
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Step 1: Portions ── */}
        {step === 1 && (
          <>
            <div className="step-header">
              <h2>How much do you need?</h2>
              <p>Set serving sizes and how many days each meal will cover.</p>
            </div>
            <div className="portion-cards">
              {meals.map(m => (
                <div key={m.tempId} className="portion-card">
                  <div className="portion-card-name">
                    {mealIcon(m)} {mealDisplayName(m)}
                  </div>

                  <div className="portion-row">
                    <div className="portion-label">
                      People
                      <span>Who are you feeding?</span>
                    </div>
                    <div className="stepper-control">
                      <button
                        className="stepper-btn"
                        disabled={m.servings <= 1}
                        onClick={() => updateMealField(m.tempId, 'servings', m.servings - 1)}
                      >−</button>
                      <span className="stepper-value">{m.servings}</span>
                      <button
                        className="stepper-btn"
                        disabled={m.servings >= 10}
                        onClick={() => updateMealField(m.tempId, 'servings', m.servings + 1)}
                      >+</button>
                    </div>
                  </div>

                  <div className="portion-row">
                    <div className="portion-label">
                      Days
                      <span>Max 3 for food safety</span>
                    </div>
                    <div className="stepper-control">
                      <button
                        className="stepper-btn"
                        disabled={m.days <= 1}
                        onClick={() => updateMealField(m.tempId, 'days', m.days - 1)}
                      >−</button>
                      <span className="stepper-value">{m.days}</span>
                      <button
                        className="stepper-btn"
                        disabled={m.days >= 3}
                        onClick={() => updateMealField(m.tempId, 'days', m.days + 1)}
                      >+</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Step 2: Add-Ons (merged Extras + Staples) ── */}
        {step === 2 && (() => {
          const stapleLabels = new Set(staples.map(s => s.label.toLowerCase()));
          const unaddedSuggestions = SUGGESTED_STAPLES.filter(
            s => !stapleLabels.has(s.label.toLowerCase())
          );
          // Group unselected suggestions by category
          const suggestionsByCategory = unaddedSuggestions.reduce<Record<string, SuggestedStaple[]>>(
            (acc, s) => {
              if (!acc[s.category]) acc[s.category] = [];
              acc[s.category].push(s);
              return acc;
            },
            {}
          );

          return (
            <>
              <div className="step-header">
                <h2>Anything else to add?</h2>
                <p>Review your weekly staples and add anything extra you need this week.</p>
              </div>

              <div className="addons-section">
                <h3 className="addons-section-title">Your weekly staples</h3>
                <p className="addons-section-desc">Items you buy consistently — check what you need this time.</p>

                {staples.length > 0 && (
                  <div className="staples-list">
                    {staples.map(s => (
                      <div key={s.id} className="staple-row">
                        <input
                          type="checkbox"
                          className="staple-check"
                          checked={Boolean(s.included)}
                          onChange={() => toggleStaple(s.id)}
                        />
                        <span className="staple-label">{s.label}</span>
                        <input
                          type="text"
                          className="staple-qty"
                          placeholder="qty"
                          value={s.quantity || ''}
                          onChange={e => updateStapleQty(s.id, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Quick-add suggestions grouped by category */}
                {Object.keys(suggestionsByCategory).length > 0 && (
                  <div className="staple-suggestions">
                    <div className="staple-suggestions-label">
                      {staples.length === 0 ? 'Quick-add common staples:' : 'Add more:'}
                    </div>
                    {Object.entries(suggestionsByCategory).map(([category, items]) => (
                      <div key={category} className="staple-suggestion-group">
                        <div className="staple-suggestion-category">{category}</div>
                        <div className="staple-suggestion-chips">
                          {items.map(s => (
                            <button
                              key={s.label}
                              className="staple-suggestion-chip"
                              onClick={() => addSuggestedStaple(s)}
                            >
                              + {s.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="add-staple-row">
                  <input
                    type="text"
                    placeholder="Add your own staple…"
                    value={newStapleLabel}
                    onChange={e => setNewStapleLabel(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addStaple()}
                  />
                  <button className="btn-add-staple" onClick={addStaple}>Add</button>
                </div>
                <span className="helper-note">Staples are saved to your profile and will pre-fill next week.</span>
              </div>

              <div className="addons-divider" />

              <div className="addons-section">
                <h3 className="addons-section-title">Anything else this week?</h3>
                <p className="addons-section-desc">Household items, snacks, or one-off things you need. We'll add them to your list.</p>
                <textarea
                  className="extras-textarea"
                  placeholder="e.g. paper towels, sparkling water, dark chocolate, olive oil"
                  value={extrasFreeform}
                  onChange={e => setExtrasFreeform(e.target.value.slice(0, 500))}
                />
                <span className="helper-note">{500 - extrasFreeform.length} characters remaining</span>
              </div>
            </>
          );
        })()}

        {/* ── Step 3: Timing ── */}
        {step === 3 && (
          <>
            <div className="step-header">
              <h2>When do you want to shop and cook?</h2>
              <p>We'll text your grocery list when it's time to shop and remind you when it's time to cook.</p>
            </div>

            <div className="timing-section">
              <h3>Grocery Shopping</h3>
              <div className="timing-row">
                <div>
                  <label>Date</label>
                  <input
                    type="date"
                    value={orderDate}
                    onChange={e => setOrderDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                  />
                </div>
                <div>
                  <label>Time</label>
                  <input
                    type="time"
                    value={orderTime}
                    onChange={e => setOrderTime(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="timing-section">
              <h3>Cook Days</h3>
              <div className="cook-times-list">
                {cookTimes.map((ct, i) => (
                  <div key={i} className="timing-row" style={{ alignItems: 'center' }}>
                    <div>
                      <label>Date</label>
                      <input
                        type="date"
                        value={ct.date}
                        onChange={e => updateCookTime(i, 'date', e.target.value)}
                        min={orderDate || new Date().toISOString().slice(0, 10)}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
                      <div style={{ flex: 1 }}>
                        <label>Time</label>
                        <input
                          type="time"
                          value={ct.time}
                          onChange={e => updateCookTime(i, 'time', e.target.value)}
                        />
                      </div>
                      {cookTimes.length > 1 && (
                        <button
                          className="btn-remove-cook"
                          onClick={() => removeCookTime(i)}
                          aria-label="Remove cook time"
                        >✕</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn-add-cook-time" onClick={addCookTime}>+ Add another cook day</button>
            </div>
          </>
        )}

        {/* ── Step 4: Review ── */}
        {step === 4 && (
          <>
            <div className="step-header">
              <h2>Review your plan</h2>
              <p>Everything look good? Hit submit and we'll build your grocery list.</p>
            </div>

            {/* Nutrition feedback */}
            {(() => {
              const missing = getMissingCategories(meals);
              if (missing.length === 0) return null;
              return (
                <div className="nutrition-feedback">
                  <div className="nutrition-feedback-title">Quick check</div>
                  {missing.map(cat => (
                    <div key={cat} className="nutrition-feedback-item">
                      {FEEDBACK_COPY[cat]}
                    </div>
                  ))}
                </div>
              );
            })()}

            <div className="review-section">
              <h3>Meals ({meals.length})</h3>
              {meals.map(m => (
                <div key={m.tempId} className="review-row">
                  <span>{mealIcon(m)} {mealDisplayName(m)}</span>
                  <span className="review-row-label">
                    {m.meal_type ? m.meal_type.charAt(0).toUpperCase() + m.meal_type.slice(1) : 'Any'} ·{' '}
                    {m.servings} {m.servings === 1 ? 'person' : 'people'} · {m.days} day{m.days > 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>

            {extrasFreeform && (
              <div className="review-section">
                <h3>Extras</h3>
                <div className="review-row">
                  <span style={{ color: 'var(--text-secondary, #555)', fontSize: '0.875rem' }}>
                    {extrasFreeform.slice(0, 80)}{extrasFreeform.length > 80 ? '…' : ''}
                  </span>
                </div>
              </div>
            )}

            {staples.filter(s => s.included).length > 0 && (
              <div className="review-section">
                <h3>Staples</h3>
                {staples.filter(s => s.included).map(s => (
                  <div key={s.id} className="review-row">
                    <span>{s.label}</span>
                    <span className="review-row-label">{s.quantity || '—'}</span>
                  </div>
                ))}
              </div>
            )}

            {(orderDate || cookTimes.some(ct => ct.date)) && (
              <div className="review-section">
                <h3>Timing</h3>
                {orderDate && (
                  <div className="review-row">
                    <span>Shopping day</span>
                    <span className="review-row-label">
                      {new Date(orderDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {orderTime ? ` at ${orderTime}` : ''}
                    </span>
                  </div>
                )}
                {cookTimes.filter(ct => ct.date).map((ct, i) => (
                  <div key={i} className="review-row">
                    <span>Cook day {cookTimes.filter(c => c.date).length > 1 ? i + 1 : ''}</span>
                    <span className="review-row-label">
                      {new Date(ct.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {ct.time ? ` at ${ct.time}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Navigation ── */}
        <div className="step-nav">
          {step > 0 && (
            <button className="btn-back" onClick={goBack}>Back</button>
          )}
          {step < TOTAL_STEPS - 1 ? (
            <button
              className="btn-next"
              disabled={!canProceedStep[step]}
              onClick={goNext}
            >
              {step === 0 && meals.length === 0 ? 'Add at least one meal' : 'Continue'}
            </button>
          ) : (
            <button
              className="btn-submit"
              disabled={submitting || meals.length === 0}
              onClick={handleSubmit}
            >
              {submitting ? 'Building your list…' : 'Build My Grocery List'}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
